// GENERADO por `npm run contratos:generar` (en backend/). No se edita a mano:
// la fuente son los catálogos de backend/src/contracts/catalogs/, y
// backend/src/contracts/generate.test.ts falla si esto no está al día.
//
// Son listas abiertas (docs/43-contratos.md §4.3): un servidor más nuevo puede
// mandar un valor que aquí no está todavía, y quien lo lea tiene que caer en
// uno por omisión en vez de romperse.

/**
 * Un módulo que se enciende negocio por negocio: lo que tiene el local, no lo
 * que puede hacer una persona.
 */
export const TENANT_MODULES = [
  'payroll',
  'whatsapp',
  'dining',
  'kitchen',
  'delivery',
  'reservations',
  'public_menu',
  'tips',
  'recipes',
  'measured_sales',
  'lots_at_sale',
  'returns',
  'variants',
  'invoicing',
  'labels',
  'presentations',
  'customer_credit',
  'shared_held_sales',
  'prescriptions',
  'serials',
  'free_lines',
  'price_lists',
  'quotes',
  'store_credit',
  'sales_commission',
] as const;

export type TenantModule = (typeof TENANT_MODULES)[number];

/** El rubro del negocio: decide con qué nace, nunca lo que se enseña. */
export const BUSINESS_TYPES = [
  'restaurant',
  'retail',
  'textile',
  'pharmacy',
  'hardware',
  'technology',
] as const;

export type BusinessType = (typeof BUSINESS_TYPES)[number];

/** Para qué sirve un equipo: decide qué pantallas abre el POS. */
export const TERMINAL_KINDS = [
  'cash_register',
  'waiter_tablet',
  'kitchen_display',
  'self_service',
  'delivery_rider',
  'phone_pos',
] as const;

export type TerminalKind = (typeof TERMINAL_KINDS)[number];

/** El estado de un pedido. */
export const ORDER_STATUSES = [
  'DRAFT',
  'CONFIRMED',
  'IN_PREPARATION',
  'READY',
  'SERVED',
  'PAYMENT_PENDING',
  'PAID',
  'CLOSED',
  'CANCELLED',
  'MERGED',
] as const;

export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** El estado de una línea del pedido. */
export const ORDER_ITEM_STATUSES = [
  'new',
  'sent_to_kitchen',
  'in_preparation',
  'ready',
  'served',
  'cancelled',
] as const;

export type OrderItemStatus = (typeof ORDER_ITEM_STATUSES)[number];

/**
 * Lo que una persona puede hacer dentro del local. Se evalúa el permiso, nunca
 * el nombre del rol.
 */
export const PERMISSIONS = [
  'order.create',
  'order.update',
  'order.cancel',
  'order.discount',
  'payment.create',
  'payment.cancel',
  'invoice.issue',
  'invoice.cancel',
  'cash.open',
  'cash.close',
  'cash.movement.create',
  'catalog.view',
  'catalog.edit',
  'dining.edit',
  'kitchen.update',
  'printer.manage',
  'terminal.manage',
  'user.manage',
  'report.view',
  'payment.method.manage',
  'catalog.availability',
  'invoice.configure',
  'order.transfer',
  'reservation.manage',
  'report.control',
  'order.discard_unclaimed',
  'invoice.void',
  'report.consolidated',
  'order.merge',
  'cash.handover',
  'payment.refund',
  'attendance.view',
  'attendance.correct',
  'complaint.read',
  'complaint.respond',
  'kitchen.view',
  'inventory.view',
  'inventory.manage',
  'inventory.purchase',
  'inventory.recipe',
  'inbox.attend',
  'customer.view',
  'customer.manage',
  'payroll.view',
  'payroll.manage',
  'delivery.view',
  'delivery.dispatch',
  'delivery.carry',
  'inventory.approve',
  'delivery.take',
  'delivery.partner.manage',
  'customer.address',
  'order.manual_sale',
  'reward.manage',
  'order.staff_meal',
  'customer_credit.sell',
  'customer_credit.view',
  'customer_credit.manage',
  'store_credit.manage',
  'sales_commission.manage',
  'order.free_line',
  'price_list.manage',
  'quote.create',
  'quote.set_price',
  'account.manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/**
 * Una excepción que se firma con un PIN: el permiso lo exige el servidor a
 * quien firma.
 */
export const AUTHORIZATION_ACTIONS = [
  'ORDER_ITEM_CANCEL',
  'ORDER_CANCEL',
  'ORDER_ITEM_DISCOUNT',
  'ORDER_DISCOUNT',
  'ORDER_DISCARD_UNCLAIMED',
  'inventory.waste',
  'TERMINAL_CONFIGURE',
  'PAYMENT_REJECTION_REVIEW',
] as const;

export type AuthorizationAction = (typeof AUTHORIZATION_ACTIONS)[number];

/**
 * Un código de error con el que un cliente decide algo. El servidor no lo
 * cambia de nombre.
 */
export const CLIENT_ERROR_CODES = [
  'TERMINAL_REVOKED',
  'ROUTE_NOT_FOUND',
  'NOT_FOUND',
  'MODIFIER_SELECTION_REQUIRED',
  'MODIFIER_TOO_MANY_SELECTIONS',
  'ORDER_ALREADY_SETTLED',
  'NO_OPEN_CASH_SESSION',
  'INVOICING_NOT_ENROLLED',
  'INVOICING_NOT_CONFIGURED',
  'CUSTOMER_REQUIRED',
  'SERIES_NOT_CONFIGURED',
  'AUTHORIZATION_REQUIRED',
  'AUTHORIZATION_INVALID',
  'TABLE_ALREADY_HAS_OPEN_ORDER',
  'TABLE_IS_JOINED',
  'FREE_LINE_ABOVE_LIMIT',
  'INVOICING_PRICE_CHANGED',
  'SECOND_FACTOR_REQUIRED',
  'PAYROLL_PERIOD_CLOSED',
  'TRAINING_NOT_ENABLED',
  'WHATSAPP_NOT_LINKED',
  'PHONE_CODE_INVALID',
  'PHONE_CODE_EXPIRED',
  'PHONE_CODE_EXHAUSTED',
  'PHONE_CODE_ALREADY_USED',
  'PHONE_ALREADY_REGISTERED',
  'FREE_TRIAL_ALREADY_USED',
  'LOT_ALREADY_EXPIRED',
] as const;

export type ClientErrorCode = (typeof CLIENT_ERROR_CODES)[number];
